import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Waves, 
  Calendar as CalendarNav, 
  MapPin,
  ArrowRight,
  Sparkles,
  Shield,
  Phone,
  MessageCircle,
  ShoppingBag,
  Globe
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useLocation } from "../context/LocationContext";
import { Footer } from "../components/layout/Footer";
import BestRightNow from "../components/BestRightNow";
import DayPlanner from "../components/DayPlanner";
import WeatherWidget from "../components/WeatherWidget";
import { API } from "../lib/api";

export default function HomePage() {
  const [property, setProperty] = useState(null);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const { language, t } = useLanguage();
  const { location, locationData, guestType, guestTypeData } = useLocation();

  const translations = {
    en: {
      yourConcierge: "YOUR LOCAL CONCIERGE",
      welcome: "Welcome to",
      tagline: "We know the best spots — so you don't have to search.",
      askLina: "Ask Lina anything",
      linaDesc: "Get personalized recommendations in seconds",
      quickLinks: "Quick access",
      beaches: "Best beaches",
      events: "What's on",
      extras: "Extras",
      emergency: "Emergency",
      parking: "Parking",
      needHelp: "Need help right now?",
      contactHost: "Contact your host directly",
      whatsapp: "WhatsApp",
      call: "Call",
      emergencyInfo: "Emergency info",
      arrive: "Arrive",
      depart: "Depart",
      select: "Select",
      monopoliTagline: "Lively beaches, boats & local flavors",
      fasanoTagline: "Luxury masserie, private chefs & exclusive clubs",
      culturalGuide: "Culture Guide"
    },
    it: {
      yourConcierge: "IL TUO CONCIERGE LOCALE",
      welcome: "Benvenuto a",
      tagline: "Conosciamo i posti migliori — così non devi cercare.",
      askLina: "Chiedi qualsiasi cosa a Lina",
      linaDesc: "Ottieni consigli personalizzati in pochi secondi",
      quickLinks: "Accesso rapido",
      beaches: "Migliori spiagge",
      events: "Cosa c'è",
      extras: "Extra",
      emergency: "Emergenza",
      parking: "Parcheggio",
      needHelp: "Hai bisogno di aiuto adesso?",
      contactHost: "Contatta direttamente il tuo host",
      whatsapp: "WhatsApp",
      call: "Chiama",
      emergencyInfo: "Info emergenza",
      arrive: "Arrivo",
      depart: "Partenza",
      select: "Seleziona",
      monopoliTagline: "Spiagge vivaci, barche e sapori locali",
      fasanoTagline: "Masserie di lusso, chef privati e club esclusivi",
      culturalGuide: "Guida Culturale"
    },
    fr: {
      yourConcierge: "VOTRE CONCIERGE LOCAL",
      welcome: "Bienvenue à",
      tagline: "Nous connaissons les meilleurs endroits — pour que vous n'ayez pas à chercher.",
      askLina: "Demandez n'importe quoi à Lina",
      linaDesc: "Obtenez des recommandations personnalisées en quelques secondes",
      quickLinks: "Accès rapide",
      beaches: "Meilleures plages",
      events: "Quoi de neuf",
      extras: "Extras",
      emergency: "Urgence",
      parking: "Parking",
      needHelp: "Besoin d'aide maintenant?",
      contactHost: "Contactez directement votre hôte",
      whatsapp: "WhatsApp",
      call: "Appeler",
      emergencyInfo: "Info urgence",
      arrive: "Arrivée",
      depart: "Départ",
      select: "Sélectionner",
      monopoliTagline: "Plages animées, bateaux et saveurs locales",
      fasanoTagline: "Masserie de luxe, chefs privés et clubs exclusifs",
      culturalGuide: "Guide Culturel"
    },
    de: {
      yourConcierge: "IHR LOKALER CONCIERGE",
      welcome: "Willkommen in",
      tagline: "Wir kennen die besten Orte — damit Sie nicht suchen müssen.",
      askLina: "Fragen Sie Lina alles",
      linaDesc: "Erhalten Sie personalisierte Empfehlungen in Sekunden",
      quickLinks: "Schnellzugriff",
      beaches: "Beste Strände",
      events: "Was ist los",
      extras: "Extras",
      emergency: "Notfall",
      parking: "Parken",
      needHelp: "Brauchen Sie jetzt Hilfe?",
      contactHost: "Kontaktieren Sie Ihren Gastgeber direkt",
      whatsapp: "WhatsApp",
      call: "Anrufen",
      emergencyInfo: "Notfall-Info",
      arrive: "Ankunft",
      depart: "Abreise",
      select: "Auswählen",
      monopoliTagline: "Lebhafte Strände, Boote und lokale Aromen",
      fasanoTagline: "Luxus-Masserie, Privatköche und exklusive Clubs",
      culturalGuide: "Kulturführer"
    },
    es: {
      yourConcierge: "TU CONCIERGE LOCAL",
      welcome: "Bienvenido a",
      tagline: "Conocemos los mejores lugares — para que no tengas que buscar.",
      askLina: "Pregunta lo que quieras a Lina",
      linaDesc: "Obtén recomendaciones personalizadas en segundos",
      quickLinks: "Acceso rápido",
      beaches: "Mejores playas",
      events: "Qué hacer",
      extras: "Extras",
      emergency: "Emergencia",
      parking: "Aparcamiento",
      needHelp: "¿Necesitas ayuda ahora?",
      contactHost: "Contacta directamente con tu anfitrión",
      whatsapp: "WhatsApp",
      call: "Llamar",
      emergencyInfo: "Info emergencia",
      arrive: "Llegada",
      depart: "Salida",
      select: "Seleccionar",
      monopoliTagline: "Playas animadas, barcos y sabores locales",
      fasanoTagline: "Masserías de lujo, chefs privados y clubes exclusivos",
      culturalGuide: "Guía Cultural"
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/property`);
        setProperty(response.data);
      } catch (e) {
        console.error("Error fetching property:", e);
      }
    };
    fetchProperty();
    
    const savedArrival = localStorage.getItem('ospitai-arrival-date');
    const savedDeparture = localStorage.getItem('ospitai-departure-date');
    if (savedArrival) setArrivalDate(new Date(savedArrival));
    if (savedDeparture) setDepartureDate(new Date(savedDeparture));
  }, []);

  const handleArrivalChange = (date) => {
    setArrivalDate(date);
    if (date) {
      localStorage.setItem('ospitai-arrival-date', date.toISOString());
      if (departureDate && date > departureDate) {
        setDepartureDate(null);
        localStorage.removeItem('ospitai-departure-date');
      }
    }
  };

  const handleDepartureChange = (date) => {
    setDepartureDate(date);
    if (date) {
      localStorage.setItem('ospitai-departure-date', date.toISOString());
    }
  };

  const quickLinks = [
    { label: txt.beaches, path: "/beaches", icon: Waves, color: "bg-sea-100 text-sea-700" },
    { label: txt.events, path: "/events", icon: CalendarNav, color: "bg-terracotta-100 text-terracotta-700" },
    { label: txt.extras, path: "/upsells", icon: ShoppingBag, color: "bg-olive-100 text-olive-700" },
    { label: txt.culturalGuide, path: "/cultural-guide", icon: Globe, color: "bg-sand-300 text-stone-700" },
    { label: txt.parking, path: "/parking", icon: MapPin, color: "bg-amber-100 text-amber-700" },
    { label: txt.emergency, path: "/emergency", icon: Shield, color: "bg-red-100 text-red-700" },
  ];

  const locationTagline = location === 'monopoli' ? txt.monopoliTagline : txt.fasanoTagline;

  return (
    <div className="min-h-screen bg-sand-200" data-testid="home-page">
      <div className="pb-8 md:pb-8">
        {/* Hero Section - Mediterranean Warm Gradient */}
        <header className="bg-gradient-to-br from-sand-300 via-sand-200 to-olive-100 text-stone-900 px-6 pt-8 pb-6 border-b border-sand-300">
          <div className="max-w-4xl mx-auto">
            <p className="text-olive-600 text-xs tracking-[0.2em] font-semibold uppercase">{txt.yourConcierge}</p>
            
            <h1 className="text-3xl md:text-4xl font-serif mt-3 text-stone-800">
              {txt.welcome} <span className="text-sea-700">{locationData?.name}</span>
            </h1>
            
            <p className="text-stone-500 text-sm mt-2">{locationTagline}</p>
          </div>
        </header>

        {/* Date Pickers & Ask Lina - Warm sand background */}
        <section className="bg-sand-100 px-6 py-6 border-b border-sand-300">
          <div className="max-w-4xl mx-auto">
            <p className="text-stone-500 text-xs mb-3">{txt.tagline}</p>
            
            {/* Date Pickers */}
            <div className="flex flex-wrap gap-3 mb-4">
              <Popover>
                <PopoverTrigger asChild>
                  <button 
                    className="bg-white border border-sand-300 hover:border-olive-500 rounded-full px-4 py-2.5 flex items-center gap-2 transition-colors min-w-[160px] shadow-sm"
                    data-testid="arrival-date-picker"
                  >
                    <CalendarIcon size={16} className="text-olive-600" />
                    <span className="text-stone-500 text-sm">{txt.arrive}</span>
                    <span className="font-semibold text-stone-900">
                      {arrivalDate ? format(arrivalDate, "MMM d") : txt.select}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={arrivalDate}
                    onSelect={handleArrivalChange}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button 
                    className="bg-white border border-sand-300 hover:border-olive-500 rounded-full px-4 py-2.5 flex items-center gap-2 transition-colors min-w-[160px] shadow-sm"
                    data-testid="departure-date-picker"
                  >
                    <CalendarIcon size={16} className="text-olive-600" />
                    <span className="text-stone-500 text-sm">{txt.depart}</span>
                    <span className="font-semibold text-stone-900">
                      {departureDate ? format(departureDate, "MMM d") : txt.select}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white" align="start">
                  <Calendar
                    mode="single"
                    selected={departureDate}
                    onSelect={handleDepartureChange}
                    disabled={(date) => date < new Date() || (arrivalDate && date < arrivalDate)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Ask Lina CTA - Warm olive gradient */}
            <Link
              to="/concierge"
              className="flex items-center gap-4 bg-gradient-to-r from-olive-600 to-olive-500 hover:from-olive-700 hover:to-olive-600 text-white rounded-2xl p-4 transition-all shadow-lg shadow-olive-600/20"
              data-testid="ask-lina-cta"
            >
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles size={20} />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{txt.askLina}</p>
                <p className="text-sm text-olive-100">{txt.linaDesc}</p>
              </div>
              <ArrowRight size={20} className="flex-shrink-0" />
            </Link>
          </div>
        </section>

        {/* Weather Widget */}
        <section className="px-6 py-4 max-w-4xl mx-auto">
          <WeatherWidget />
        </section>

        {/* Best Right Now - The Killer Feature */}
        <BestRightNow />

        {/* Day Planner */}
        <DayPlanner />

        {/* Quick Links */}
        <section className="px-6 py-6 max-w-4xl mx-auto">
          <h2 className="text-lg font-semibold text-stone-800 mb-4">{txt.quickLinks}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="bg-white rounded-2xl p-4 border border-sand-300 hover:border-olive-400 hover:shadow-lg hover:shadow-olive-600/10 transition-all group text-center"
                >
                  <div className={`w-12 h-12 ${link.color} rounded-xl flex items-center justify-center mx-auto mb-2`}>
                    <Icon size={22} />
                  </div>
                  <p className="font-medium text-stone-700 text-sm group-hover:text-olive-700 transition-colors">
                    {link.label}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Help Section - Deep sea blue */}
        <section className="px-6 py-6 mb-20 md:mb-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-sea-700 to-sea-800 rounded-2xl p-6 text-white shadow-xl shadow-sea-700/20">
            <h3 className="font-serif text-xl">{txt.needHelp}</h3>
            <p className="text-sea-200 text-sm mt-1">{txt.contactHost}</p>
            
            <div className="flex gap-3 mt-5">
              <a 
                href={`https://wa.me/${property?.host_whatsapp || locationData?.whatsapp || "393331234567"}`}
                className="flex-1 bg-olive-600 hover:bg-olive-500 py-3 px-4 rounded-xl text-sm font-medium text-center transition-colors flex items-center justify-center gap-2"
                data-testid="whatsapp-btn"
              >
                <MessageCircle size={16} />
                {txt.whatsapp}
              </a>
              <a 
                href={`tel:${property?.host_phone || "+39 080 000 0000"}`}
                className="flex-1 bg-white/10 hover:bg-white/20 py-3 px-4 rounded-xl text-sm font-medium text-center transition-colors flex items-center justify-center gap-2"
                data-testid="call-btn"
              >
                <Phone size={16} />
                {txt.call}
              </a>
            </div>
            <Link to="/emergency" className="block mt-4 text-center text-terracotta-400 hover:text-terracotta-300 text-sm font-medium">
              {txt.emergencyInfo}
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
